/**
 * Number, date and address formatting of the lock pages. Pure and locale-independent (always en-US, always UTC) so the server render, the
 * browser, the PNG and the tests print the same text. A missing number prints as an em dash, never as zero.
 */
import { getAddress } from 'viem'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const p2 = (n: number) => String(n).padStart(2, '0')

/** 16 Mar 2028 */
export function fmtDay(ts: number | null | undefined): string {
  if (ts === null || ts === undefined || !Number.isFinite(ts)) return '—'
  const d = new Date(ts * 1000)
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}
/** 16 Mar 2028, 04:00 UTC */
export function fmtDateTime(ts: number | null | undefined): string {
  if (ts === null || ts === undefined || !Number.isFinite(ts)) return '—'
  const d = new Date(ts * 1000)
  return `${fmtDay(ts)}, ${p2(d.getUTCHours())}:${p2(d.getUTCMinutes())} UTC`
}
/** the value of a `datetime-local` style input in UTC: 2028-03-16T04:00 */
export const toInputUtc = (ts: number) => new Date(ts * 1000).toISOString().slice(0, 16)
export const fromInputUtc = (v: string): number | null => {
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v)) return null
  const t = Date.parse(`${v}:00Z`)
  return Number.isFinite(t) ? Math.floor(t / 1000) : null
}

export type Parts = { d: number; h: number; m: number; s: number }
export function countdownParts(seconds: number): Parts {
  const s = Math.max(0, Math.floor(seconds))
  return { d: Math.floor(s / 86400), h: Math.floor((s % 86400) / 3600), m: Math.floor((s % 3600) / 60), s: s % 60 }
}
/** 2d 04:11:09 style, for a live clock in a monospace face */
export function fmtClock(seconds: number): string {
  const { d, h, m, s } = countdownParts(seconds)
  return `${d > 0 ? `${d}d ` : ''}${p2(h)}:${p2(m)}:${p2(s)}`
}

const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
/**
 * How long, in words, with at most two units: "2 years 5 months", "11 months", "45 days", "3 days 4 hours", "5 hours 12 min", "9 min".
 * Months are 30 days and years 365, which is how people read a lock's length (the exact date is always printed beside it).
 */
export function fmtSpan(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  if (s < 60) return plural(s, 'second')
  if (s < 3600) return `${Math.floor(s / 60)} min`
  const days = Math.floor(s / 86400)
  if (days < 1) {
    const h = Math.floor(s / 3600)
    const m = Math.floor((s % 3600) / 60)
    return m ? `${plural(h, 'hour')} ${m} min` : plural(h, 'hour')
  }
  if (days < 3) {
    const h = Math.floor((s % 86400) / 3600)
    return h ? `${plural(days, 'day')} ${plural(h, 'hour')}` : plural(days, 'day')
  }
  if (days < 60) return plural(days, 'day')
  if (days < 365) {
    const mo = Math.floor(days / 30)
    return plural(mo, 'month')
  }
  const y = Math.floor(days / 365)
  const mo = Math.floor((days % 365) / 30)
  return mo ? `${plural(y, 'year')} ${plural(mo, 'month')}` : plural(y, 'year')
}

/** The same in the shortest honest form, for a figure that must stay on one line: 2y 5mo, 6mo, 45d, 3d 4h, 5h 12m, 9m. */
export function fmtSpanShort(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  const days = Math.floor(s / 86400)
  if (days >= 365) {
    const mo = Math.floor((days % 365) / 30)
    return mo ? `${Math.floor(days / 365)}y ${mo}mo` : `${Math.floor(days / 365)}y`
  }
  if (days >= 60) return `${Math.floor(days / 30)}mo`
  if (days >= 3) return `${days}d`
  if (days >= 1) return `${days}d ${Math.floor((s % 86400) / 3600)}h`
  if (s >= 3600) return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
  return s >= 60 ? `${Math.floor(s / 60)}m` : `${s}s`
}

/** Basis points as a percentage: 83.2%, 0.35%, <0.01%, 100%. */
export function fmtShare(bps: number | null | undefined): string {
  if (bps === null || bps === undefined || !Number.isFinite(bps)) return '—'
  if (bps <= 0) return '0%'
  if (bps < 1) return '<0.01%'
  if (bps >= 9995) return '100%'
  const pct = bps / 100
  return `${Number(pct.toFixed(pct >= 10 ? 1 : 2))}%`
}

/** $12,430 · $1.24M · $0.42 · <$0.01 */
export function fmtUsd(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—'
  if (n <= 0) return '$0'
  if (n < 0.01) return '<$0.01'
  if (n < 1) return `$${n.toFixed(2)}`
  if (n < 100_000) return `$${n.toLocaleString('en-US', { maximumFractionDigits: n < 1000 ? 2 : 0 })}`
  return `$${new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 }).format(n)}`
}

/** A raw token amount as text: 1,234.56 · 30B · 0.000123 · < 0.000001, never in e-notation. `full` keeps every digit that matters (tooltips and the certificate's amount line). */
export function fmtUnits(raw: bigint | string | null | undefined, decimals: number, full = false): string {
  if (raw === null || raw === undefined || raw === '') return '—'
  const v = BigInt(raw)
  const base = 10n ** BigInt(decimals)
  const whole = v / base
  const frac = v % base
  const asNumber = Number(whole) + Number(frac) / Number(base)
  // never scientific notation (Bez 2026-10-09, "+ 3.83e-7 USDC" on a certificate): a non-zero amount under one millionth reads "< 0.000001"
  const tiny = v > 0n && whole === 0n && frac * 1_000_000n < base
  if (full) {
    if (tiny) return '< 0.000001'
    const f = decimals > 0 ? frac.toString().padStart(decimals, '0').slice(0, 6).replace(/0+$/, '') : ''
    return `${whole.toLocaleString('en-US')}${f ? `.${f}` : ''}`
  }
  if (v === 0n) return '0'
  if (tiny) return '< 0.000001'
  if (asNumber >= 1_000_000) return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 }).format(asNumber)
  if (asNumber >= 1) return asNumber.toLocaleString('en-US', { maximumFractionDigits: 2 })
  return Number(asNumber.toPrecision(3)).toString()
}

/** EIP-55 checksum of an address (the form BscScan shows); anything that is not an address comes back unchanged. */
export function checksum(a?: string | null): string {
  if (!a) return ''
  try {
    return getAddress(a)
  } catch {
    return a
  }
}
export const shortAddress = (a?: string | null) => (a ? `${checksum(a).slice(0, 6)}…${checksum(a).slice(-4)}` : '')
export const shortHash = (h?: string | null) => (h ? `${h.slice(0, 10)}…${h.slice(-6)}` : '')
export const isAddr = (a: unknown): boolean => typeof a === 'string' && /^0x[0-9a-fA-F]{40}$/.test(a)
export const lc = (a?: string | null) => (a ?? '').toLowerCase()
